import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { LogIn, Compass } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { useAuthDialog } from "@/context/AuthDialogContext";
import { PathChoice, isReturning, RETURNING_KEY } from "@/components/PathChoice";

/** Returning device (already reached the control panel once): straight to login, no landing question. */
function ReturningLogin() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const authDialog = useAuthDialog();
  const it = lang === "IT";
  const open = () => authDialog.openAuth(() => navigate("/app/home"));
  useEffect(() => { const id = setTimeout(open, 350); return () => clearTimeout(id); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="min-h-[100svh] bg-white flex flex-col items-center justify-center px-5 text-center" data-testid="returning-login">
      <img src="/cpenz-icon-flat.png" alt="cpenz" className="h-12 w-12" style={{ filter: "brightness(0)" }} />
      <h1 className="mt-6 cpenz-display text-3xl sm:text-4xl text-[#0b0f1a]">{it ? "Bentornato su cpenz.io" : "Welcome back to cpenz.io"}</h1>
      <p className="mt-3 text-slate-600">{it ? "Accedi per tornare al tuo pannello di controllo." : "Log in to get back to your control panel."}</p>
      <button type="button" onClick={open} data-testid="returning-login-btn" className="mt-8 inline-flex h-12 items-center gap-2 rounded-full bg-[#0b0f1a] px-7 text-sm font-semibold text-white hover:bg-black"><LogIn size={16} /> {it ? "Accedi" : "Log in"}</button>
      <button type="button" onClick={() => { localStorage.removeItem(RETURNING_KEY); window.location.reload(); }} data-testid="returning-reset" className="mt-6 text-xs font-semibold text-slate-400 hover:text-[#0b0f1a]">{it ? "Non sei tu? Vai alla pagina iniziale" : "Not you? Go to the start page"}</button>
      <button type="button" onClick={() => navigate("/community")} data-testid="returning-explore-guest" className="mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-slate-400 hover:bg-[#f4f6fa] hover:text-[#0b0f1a]"><Compass size={13} /> {it ? "Esplora come visitatore" : "Explore as a guest"}</button>
    </div>
  );
}

/** Public landing: one full-screen question with two paths (CREATE / BUILD). Everything else lives in /varie. */
export default function Landing() {
  const navigate = useNavigate();
  const { user, ready } = useAuth();
  const authDialog = useAuthDialog();
  const reduce = useReducedMotion();
  const returning = isReturning();
  const { lang } = useLang();

  useEffect(() => { if (ready && user && returning) navigate("/app/home", { replace: true }); }, [ready, user, returning, navigate]);
  useEffect(() => {
    const warm = () => { import("@/pages/IdeaFlow"); import("@/pages/BuildFlow"); };
    const id = "requestIdleCallback" in window ? window.requestIdleCallback(warm, { timeout: 4000 }) : setTimeout(warm, 2500);
    return () => ("cancelIdleCallback" in window ? window.cancelIdleCallback(id) : clearTimeout(id));
  }, []);

  if (!ready) return <div className="min-h-[100svh] bg-[#f5f5f7]" data-testid="landing-waiting" />;
  if (returning) return user ? <div className="min-h-[100svh] bg-[#f5f5f7]" /> : <ReturningLogin />;
  return (
    <div className="bg-[#f5f5f7] overflow-x-hidden">
      <section id="hero" className="relative min-h-[100svh] flex flex-col items-center justify-center overflow-hidden px-4 sm:px-6 py-10 sm:py-14">
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 24, scale: 0.985 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="cpenz-panel relative w-full max-w-5xl mx-auto px-6 py-14 sm:px-12 sm:py-20 md:px-16 shadow-2xl border border-white/10"
          data-testid="hero-center"
        >
          <p className="mb-6 flex justify-center"><span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3.5 py-1.5 text-xs font-medium text-white/90" data-testid="hero-eyebrow"><span className="h-2 w-2 rounded-full bg-[#7c3aed] animate-pulse" /> cpenz.io · Idea OS</span></p>
          <PathChoice dark />
        </motion.div>
        <p className="mt-8 flex flex-wrap items-center justify-center gap-x-1 px-5 text-center text-xs font-medium text-[#86868b]" data-testid="landing-guest-row">
          <button type="button" onClick={() => navigate("/community")} data-testid="landing-explore-guest" className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 transition-colors hover:bg-black/5 hover:text-[#1d1d1f]">
            <Compass size={13} /> {lang === "IT" ? (user ? "Esplora la community" : "Esplora come visitatore") : (user ? "Explore the community" : "Explore as a guest")}
          </button>
          {!user && (<>
            <span>{lang === "IT" ? "o" : "or"}</span>
            <button type="button" onClick={() => authDialog.openAuth(() => navigate("/app/home"))} data-testid="landing-login-link" className="rounded-full px-2 py-1 text-[#1d1d1f] underline decoration-black/20 underline-offset-2 transition-colors hover:bg-black/5">
              {lang === "IT" ? "accedi" : "log in"}
            </button>
            <span>{lang === "IT" ? "se sei già un utente" : "if you already have an account"}</span>
          </>)}
        </p>
      </section>
    </div>
  );
}
